import { useEffect, useMemo, useState } from "react";
import { getEnv } from "../lib/environment";
import { useWorkspaceContext } from "../state/workspaceContext";
import { useUserStatus } from "./useUserStatus";
import { useBillingStatus } from "./useBillingStatus";
import { useTypedFlags } from "./useTypedFlags";

export function usePlainChat() {
    const { plainChat } = useTypedFlags();
    const [retry, setRetry] = useState(0);
    const chatAppId = getEnv("VITE_PLAIN_CHAT_APP_ID");

    const chatConfig = useMemo(() => {
        const chatConfigRaw = getEnv("VITE_PLAIN_CHAT_CONFIG");

        if (!chatConfigRaw) {
            return [];
        }

        try {
            return JSON.parse(chatConfigRaw);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Error parsing chat config", e);
            return [];
        }
    }, []);

    const { data: userStatusResponse, isFetching: isFetchingUserStatus } =
        useUserStatus();
    const { product, isFetched: isBillingStatusFetched } = useBillingStatus();

    const { activeWorkspaceId } = useWorkspaceContext();

    useEffect(() => {
        if (!plainChat) {
            return;
        }

        if (
            isFetchingUserStatus ||
            !userStatusResponse ||
            !isBillingStatusFetched
        ) {
            return;
        }
        const { user, plainHash } = userStatusResponse;

        if (!user || !plainHash) {
            return;
        }

        if (!chatAppId?.startsWith("liveChatApp")) {
            return;
        }

        if (!window.Plain) {
            // cheat to wait for the script to load
            setTimeout(() => setRetry(retry + 1), 1000);
            return;
        }

        if (window.Plain.isInitialized()) {
            return;
        }

        window.Plain.init({
            appId: chatAppId,
            hideLauncher: true,
            customerDetails: {
                email: user.email,
                emailHash: plainHash,
                fullName: user.name,
                shortName: user.firstName,
                chatAvatarUrl: user.avatar ?? "",
            },
            links: [
                {
                    icon: "book",
                    text: "View our docs",
                    url: "https://kickfinance.notion.site/kick-help-center",
                },
            ],
            entryPoint: {
                type: "default",
            },
            hideBranding: true,
            theme: "light",

            style: {
                brandColor: "#1a78c0",
                brandBackgroundColor: "#1a78c0",
                launcherBackgroundColor: "#0F1826",
                launcherIconColor: "#FFFFFF",
            },

            position: {
                right: "calc(100% - 432px)",
                bottom: "50px",
            },

            threadDetails: {
                tierIdentifier: { externalId: product },
                tenantIdentifier: { externalId: activeWorkspaceId },
            },

            chatButtons: chatConfig,
        });
    }, [
        activeWorkspaceId,
        chatAppId,
        chatConfig,
        userStatusResponse,
        isFetchingUserStatus,
        retry,
        isBillingStatusFetched,
        product,
        plainChat,
    ]);
}
